import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logoImage from "./logo.png";
import cirruslabsImage from "./cirruslabs.png";
import { useAzureAuth } from "../auth";
import { useAuth } from "../auth/AuthContext";

const Login = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    
    // Azure SSO hook - must be called before using handleRedirectResponse
    const { 
      loginWithMicrosoft, 
      handleRedirectResponse,
      isAzureSSOConfigured,
      loading: msLoading, 
      error: azureError,
      clearError: clearAzureError 
    } = useAzureAuth();
    
    const [redirectHandled, setRedirectHandled] = useState(false);

    // Handle Azure redirect response FIRST, before checking token
    useEffect(() => {
      const checkRedirectResponse = async () => {
        try {
          const result = await handleRedirectResponse();
          if (result?.success) {
            navigate("/dashboard", { replace: true });
          }
        } catch (err) {
          console.error("❌ [LoginPage] Error in handleRedirectResponse:", err);
          console.error("❌ [LoginPage] Error details:", {
            message: err.message,
            stack: err.stack
          });
        } finally {
          setRedirectHandled(true);
        }
      };
      
      checkRedirectResponse();
    }, [handleRedirectResponse, navigate]);

    // If already authenticated, don't stay on /login (prevents SSO redirect loops)
    // BUT wait until redirect is handled to avoid race conditions
    useEffect(() => {
      if (!redirectHandled) {
        return; // Wait for redirect handling to complete
      }
      
      const lsToken = localStorage.getItem("access_token");
      if (token || lsToken) {
        navigate("/dashboard", { replace: true });
      }
    }, [token, redirectHandled, navigate]);
    
  const [emptyError, setEmptyError] = useState("");

  // Remove the duplicate handleRedirectResponse useEffect - it's now at the top

  // Show Azure errors
  useEffect(() => {
    if (azureError) {
      setEmptyError(azureError);
    }
  }, [azureError]);

  const handleMicrosoftSignIn = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setEmptyError("");
    clearAzureError();
    
    if (!isAzureSSOConfigured) {
      setEmptyError("Microsoft SSO is not configured. Please contact your administrator.");
      return;
    }

    try {
      const result = await loginWithMicrosoft();
      
      if (result && result.success) {
        navigate("/dashboard");
      } else if (result && result.error) {
        // Don't show error if user cancelled the popup
        if (!result.error.includes("user_cancelled") && !result.error.includes("popup_window_error")) {
          setEmptyError(result.error);
        }
      }
    } catch (err) {
      console.error("Microsoft SSO error:", err);
      // Don't show error if user cancelled
      if (!err.message?.includes("user_cancelled")) {
        setEmptyError(err.message || "Microsoft sign-in failed");
      }
    }
  };

  // Generate animated dots for background
  const generateDots = () => {
    // Generate dots only once and keep them static
    if (!window._staticDots) {
      const dots = [];
      for (let i = 0; i < 120; i++) {
        const style = {
          position: "absolute",
          width: `${Math.random() * 4 + 2}px`,
          height: `${Math.random() * 4 + 2}px`,
          backgroundColor: `rgba(255, 255, 255, ${Math.random() * 0.3 + 0.1})`,
          borderRadius: "50%",
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `float-dots ${Math.random() * 3 + 2}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        };
        dots.push(<div key={i} style={style} />);
      }
      window._staticDots = dots;
    }
    return window._staticDots;
  };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* LEFT SIDE - Dark Blue with Animation */}
      <div
        style={{
          width: "50%",
          background: "linear-gradient(135deg, #1e3a8a 0%, #1b2a5aff 50%, #1a2d57ff 100%)",
          padding: "2.64rem 2.2rem", // +10% from 2.4rem 2rem
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated dots background */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {generateDots()}
        </div>

        {/* Radial gradient overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* Content */}
        <div style={{ position: "relative", zIndex: 10, textAlign: "center", padding: "2rem" }}>
          <img
            src={cirruslabsImage}
            alt="CirrusLabs"
            style={{
              width: "240px",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.2))",
              zIndex: 20,
            }}
          />
          <div style={{ position: "relative", zIndex: 21, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "70vh" }}>
            <img
              src={logoImage}
              alt="Portal Logo"
              style={{
                width: "120px",
                marginBottom: "2rem",
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.2))",
              }}
            />
            <h1
              style={{
                color: "#9084a9ff",
                fontSize: "52px",
                fontWeight: 900,
                marginBottom: "0.2rem",
                textShadow: "0 2px 20px rgba(0, 0, 0, 0.3)",
                letterSpacing: "-0.02em",
                textAlign: "center",
              }}
            >
              Deal Manager
            </h1>
            <p
              style={{
                color: "rgba(138, 131, 154, 1)",
                fontSize: "18px",
                fontWeight: 500,
                marginBottom: "1.2rem",
                letterSpacing: "0.5px",
                textAlign: "center",
              }}
            >
             • Companies • Customers • Deals • Management 
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - White Background with Login Form */}
      <div
        style={{
          width: "50%",
          background: "#f9fafb",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "2rem",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            background: "#ffffff",
            padding: "3rem 2.5rem",
            borderRadius: "13px", // 20% reduction from 16px
            boxShadow: "0 8px 32px rgba(0,0,0,0.09)",
          }}
        >
          <h2
            style={{
              fontSize: "30px",
              fontWeight: 800,
              marginBottom: "1.6rem", // 20% reduction from 2rem
              color: "#1c4fa3ff",
              textAlign: "center",
            }}
          >
            Login
          </h2>
          {emptyError && (
            <p role="alert" style={{ color: "#ef4444", fontWeight: 600, textAlign: "center", margin: "0.5rem 0 1rem 0" }}>{emptyError}</p>
          )}

          {/* Microsoft SSO Button */}
          <button
            type="button"
            onClick={handleMicrosoftSignIn}
            disabled={msLoading}
            aria-label="Sign in with Microsoft"
            aria-busy={msLoading}
            style={{
              width: "100%",
              height: "50px", // +10% from 38px
              borderRadius: "8px", // 20% reduction from 10px
              border: "1.6px solid #e2e8f0", // 20% reduction from 2px
              background: "#ffffff",
              fontSize: "16px", 
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "9.6px", // 20% reduction from 12px
              cursor: msLoading ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              opacity: msLoading ? 0.6 : 1,
              color: "#334155",
            }}
            onMouseEnter={(e) => {
              if (!msLoading) {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#cbd5e1";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
              }
            }}
            onMouseLeave={(e) => {
              if (!msLoading) {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }
            }}
          >
            {msLoading ? (
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "3px solid #e2e8f0",
                  borderTop: "3px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <rect width="11" height="11" fill="#F35325" />
                  <rect y="13" width="11" height="11" fill="#05A6F0" />
                  <rect x="13" width="11" height="11" fill="#81BC06" />
                  <rect x="13" y="13" width="11" height="11" fill="#FFBA08" />
                </svg>
                <span>Sign in with Microsoft</span>
              </>
            )}
          </button>

          <div style={{ marginTop: 15, paddingTop: 12, color: '#64748b', fontSize: 13, textAlign: 'center' }}>
            Sign in with your @cirruslabs.io account
          </div>
        </div>
      </div>

      
      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes float-dots {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .shake {
          animation: shake 0.3s;
        }
        @keyframes shake {
          0% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default Login;
